'use client';

import { FC, useEffect, useState } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { DisneySelectionModal } from '@telialt/common-disney-selection-modal/src/DisneySelectionModal';
import { NetflixSelectionModal } from '@telialt/common-netflix-selection-modal/src/NetflixSelectionModal';
import { DISNEY_PLAN, NETFLIX_PLAN } from '@telialt/utils/src/eshopConstants';
import { withParams } from '@telialt/utils/src/helpers';
import { useSegmentSlug } from '@telialt/utils/src/segment';

import { InitiateCheckoutProps } from '../../../../hooks/useInitiateSelfServiceCheckout';
import { useDisneySelectionModalControls } from '../../hooks/useDisneySelectionModalControls';
import { useNetflixSelectionModalControls } from '../../hooks/useNetflixSelectionModalControls';
import {
    CHECKOUT_URL,
    CONTRACT_PROLONGATION_LINK,
    DISNEY_VAS_OPTION_KEY,
    MODEM_SELECTION_URL,
    PARAM_DISNEY_QUERY,
    PARAM_GIFTS_QUERY,
    PARAM_NETFLIX_QUERY,
    PARAM_SERVICE_SO_ID_QUERY,
    PARAM_SERVICE_TYPE_QUERY,
    PARAM_SERVICE_UO_ID_QUERY,
    PLAN_ORDER_OPTION,
} from './constants';
import { GiftPreviewModal, GiftSelectionModal, useGiftSelectionModalControls } from './GiftSelection';
import { useGiftPreviewModalControls } from './GiftSelection/hooks/useGiftPreviewModalControls';
import { PlanOrderFlowData, PlanOrderOption } from './model';
import { PlanOrderLeadModal } from './PlanOrderLeadModal/PlanOrderLeadModal';
import { usePlanOrderLeadModalControls } from './PlanOrderLeadModal/usePlanOrderLeadModalControls';
import { PlanOrderOptionSelectModal } from './PlanOrderOptionSelectModal/PlanOrderOptionSelectModal';
import { usePlanOrderOptionSelectModalControls } from './PlanOrderOptionSelectModal/usePlanOrderOptionSelectModalControls';
import { usePlanOrderingFlow } from './usePlanOrderingFlow';

type Props = {
    dataList: PlanOrderFlowData[];
    onCheckout?: (props: InitiateCheckoutProps) => void;
};

export const PlanOrderingFlowController: FC<Props> = ({ dataList, onCheckout }) => {
    const { t } = useTranslations();
    const segmentSlug = useSegmentSlug();
    const { openPlanOrderOptionSelectModal, editPlanOrderOptionSelectModal } = usePlanOrderOptionSelectModalControls();
    const { openGiftSelectionModal, editGiftSelectionModal } = useGiftSelectionModalControls();
    const { uidForGiftPreviewModal } = useGiftPreviewModalControls();
    const { openPlanOrderLeadModal } = usePlanOrderLeadModalControls();
    const { isDisneySelectionModalOpen, openDisneySelectionModal, closeDisneySelectionModal } =
        useDisneySelectionModalControls();
    const { isNetflixSelectionModalOpen, openNetflixSelectionModal, closeNetflixSelectionModal } =
        useNetflixSelectionModalControls();

    const { isPlanOrderFlowStarted, resetStartPlanOrderFlow, uidForModal } = usePlanOrderingFlow();

    const [giftsValue, setGiftsValue] = useState<string[]>([]);

    const data = dataList.find((item) => item.uidForModal === uidForModal);

    const {
        orderActionType,
        serviceType,
        serviceUoId,
        teliaOneOfferId,
        minimumGifts,
        gifts,
        giftsDescription,
        giftsName,
        onSelectModifyService,
        onSelectNewContract,
        code,
        nboId,
        offerSpecificationId,
        gaTracking,
        offerGroup,
        nboElementPk,
    } = data || {};

    const hasGifts = Boolean(gifts?.length);
    const isCheckout = orderActionType === 'CHECKOUT';
    const isNetflixCheckout = orderActionType === 'NETFLIX_CHECKOUT';
    const isDisneyCheckout = orderActionType === 'DISNEY_CHECKOUT';
    const isSsCheckout = Boolean(onCheckout);
    const isLead = orderActionType === 'LEAD';
    const isMBB = serviceType === 'MBB';

    const queryBase = {
        [PARAM_SERVICE_TYPE_QUERY]: serviceType,
        [PARAM_SERVICE_UO_ID_QUERY]: serviceUoId,
        ...(teliaOneOfferId && { [PARAM_SERVICE_SO_ID_QUERY]: teliaOneOfferId }),
    };
    const generateModemSelectionLink = (query?: Record<string, string>) =>
        `${withParams(MODEM_SELECTION_URL, { segmentSlug })}?${new URLSearchParams({ ...queryBase, ...query })}`;
    const generateCheckoutLink = (query?: Record<string, string>) =>
        `${withParams(CHECKOUT_URL, { segmentSlug })}?${new URLSearchParams({ ...queryBase, ...query })}`;
    const streamingDefaultQuery = (() => {
        if (isNetflixCheckout) {
            return { [PARAM_NETFLIX_QUERY]: NETFLIX_PLAN.STANDARD };
        }

        if (isDisneyCheckout) {
            return { [PARAM_DISNEY_QUERY]: DISNEY_PLAN.STANDARD };
        }

        return undefined;
    })();

    useEffect(() => {
        if (isPlanOrderFlowStarted) {
            resetStartPlanOrderFlow();
            setGiftsValue([]);

            if (isCheckout && isSsCheckout) {
                onCheckout({
                    code,
                    serviceUoId,
                    nboId,
                    offerSpecificationId,
                    offerGroup,
                    teliaOneOfferId,
                    nboElementPk,
                });
            } else if (isDisneyCheckout && isSsCheckout) {
                openDisneySelectionModal();
            } else if ((isCheckout || isNetflixCheckout || isDisneyCheckout) && isMBB && hasGifts) {
                openGiftSelectionModal();
            } else if ((isCheckout || isNetflixCheckout || isDisneyCheckout) && isMBB) {
                window.location.assign(generateModemSelectionLink(streamingDefaultQuery));
            } else if (isCheckout || isNetflixCheckout || isDisneyCheckout) {
                openPlanOrderOptionSelectModal();
            } else if (isLead && hasGifts) {
                openGiftSelectionModal();
            } else if (isLead) {
                openPlanOrderLeadModal({ leadText: leadTextRegular, gaTracking });
            }
        }
    }, [isPlanOrderFlowStarted /* should observe only "isPlanOrderFlowStarted", to start Plan ordering flow */]);

    // Generate lead text
    const { hasTelia1, planName, pricing, commitmentType } = data || {};
    const messageUsed =
        serviceType &&
        (hasTelia1
            ? t[`plans.${serviceType.toLowerCase()}.leads_modal.telia1_plan`]
            : t[`plans.${serviceType.toLowerCase()}.leads_modal.plan`]);
    const planPrice = (pricing?.new || pricing?.regular)?.toString();

    const leadTextRegular = withParams(messageUsed, { commitmentType, planName, planPrice });
    const leadTextWithGifts =
        hasGifts &&
        `${leadTextRegular} ; dovanos: `.concat(
            gifts?.map((gift) => `produktoId: ${gift.id} ; kaina: ${gift.price}`).join(' ; '),
        );
    // End lead text generation

    const onGiftSelected = (selectedGifts: string[]) => {
        const selectedGiftsQueryParam =
            selectedGifts.length > 0 ? { [PARAM_GIFTS_QUERY]: selectedGifts.join(',') } : {};

        if (isCheckout) {
            if (isMBB) {
                window.location.assign(generateModemSelectionLink({ ...selectedGiftsQueryParam }));
            } else {
                window.location.assign(generateCheckoutLink({ ...selectedGiftsQueryParam }));
            }
        } else if (isMBB && (isNetflixCheckout || isDisneyCheckout)) {
            if (streamingDefaultQuery) {
                window.location.assign(
                    generateModemSelectionLink({
                        ...selectedGiftsQueryParam,
                        ...streamingDefaultQuery,
                    }),
                );
            }
        } else if (isNetflixCheckout || isDisneyCheckout) {
            setGiftsValue(selectedGifts);
            editGiftSelectionModal(false);

            if (isNetflixCheckout) {
                openNetflixSelectionModal();
            } else if (isDisneyCheckout) {
                openDisneySelectionModal();
            }
        } else if (isLead) {
            editGiftSelectionModal(false);
            openPlanOrderLeadModal({ leadText: leadTextWithGifts, gaTracking });
        }
    };
    const onOrderOptionSelected = (selectedOption: PlanOrderOption) => {
        if (selectedOption === PLAN_ORDER_OPTION.PROLONGATION) {
            onSelectModifyService?.();

            window.location.assign(withParams(CONTRACT_PROLONGATION_LINK, { segmentSlug }));
        } else if (selectedOption === PLAN_ORDER_OPTION.NEW_CONTRACT) {
            onSelectNewContract?.();

            if (hasGifts) {
                editPlanOrderOptionSelectModal(false);
                openGiftSelectionModal();
            } else if (isNetflixCheckout) {
                editPlanOrderOptionSelectModal(false);
                openNetflixSelectionModal();
            } else if (isDisneyCheckout) {
                editPlanOrderOptionSelectModal(false);
                openDisneySelectionModal();
            } else {
                window.location.assign(generateCheckoutLink());
            }
        }
    };

    const dataForPreview = dataList.find((item) => item.uidForModal === uidForGiftPreviewModal);

    const handleNetflixSelect = (planType: (typeof NETFLIX_PLAN)[keyof typeof NETFLIX_PLAN]) => {
        const query: Record<string, string> = { [PARAM_NETFLIX_QUERY]: planType };

        if (giftsValue.length) {
            query[PARAM_GIFTS_QUERY] = giftsValue.join(',');
        }

        window.location.assign(generateCheckoutLink(query));
    };
    const handleDisneySelect = (planType: (typeof DISNEY_PLAN)[keyof typeof DISNEY_PLAN]) => {
        if (onCheckout) {
            onCheckout({
                code,
                serviceUoId,
                nboId,
                offerSpecificationId,
                offerGroup,
                teliaOneOfferId,
                vasOptions: {
                    [DISNEY_VAS_OPTION_KEY]: planType,
                },
            });
        } else {
            const query: Record<string, string> = { [PARAM_DISNEY_QUERY]: planType };

            if (giftsValue.length) {
                query[PARAM_GIFTS_QUERY] = giftsValue.join(',');
            }

            window.location.assign(generateCheckoutLink(query));
        }
    };

    return (
        <>
            <GiftPreviewModal
                title={dataForPreview?.giftsName}
                gifts={dataForPreview?.gifts}
                description={dataForPreview?.giftsDescription}
            />
            <GiftSelectionModal
                gifts={gifts}
                title={giftsName}
                description={giftsDescription}
                minimumGifts={minimumGifts}
                onSelect={onGiftSelected}
                onCancel={resetStartPlanOrderFlow}
            />
            <PlanOrderOptionSelectModal
                onSelect={onOrderOptionSelected}
                serviceType={serviceType}
                onCancel={resetStartPlanOrderFlow}
            />
            <PlanOrderLeadModal onCancel={resetStartPlanOrderFlow} />
            <DisneySelectionModal
                isOpen={isDisneySelectionModalOpen}
                onClose={() => {
                    closeDisneySelectionModal();
                    resetStartPlanOrderFlow();
                }}
                onStandardClick={() => handleDisneySelect(DISNEY_PLAN.STANDARD)}
                onPremiumClick={() => handleDisneySelect(DISNEY_PLAN.PREMIUM)}
            />
            <NetflixSelectionModal
                isOpen={isNetflixSelectionModalOpen}
                onClose={() => {
                    closeNetflixSelectionModal();
                    resetStartPlanOrderFlow();
                }}
                title={t['plans.netflix_selection_modal.title']}
                description={t['plans.netflix_selection_modal.description']}
                onStandardClick={() => handleNetflixSelect(NETFLIX_PLAN.STANDARD)}
                onPremiumClick={() => handleNetflixSelect(NETFLIX_PLAN.PREMIUM)}
            />
        </>
    );
};
